/**
 * useAssessmentDetails Hook
 * 
 * Custom hook for fetching and managing assessment details only.
 * Handles loading states, error handling, and data synchronization with Redux.
 * Also syncs risk ranking changes with hierarchySlice (Process <-> RiskAssessment 1:1 mapping).
 * 
 * Note: Qualitative factors are managed separately in Tab1ProcessLevelRiskRanking.
 * 
 * @param assessmentId - The assessment ID to fetch data for
 * @param processCode - Optional process code for syncing with hierarchySlice
 * @param cycleCode - Optional cycle code for syncing with hierarchySlice
 * @returns Object containing loading state, error, and refetch function
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { message } from 'antd';
import { AppDispatch } from '../store';
import {
  getAssessmentDetails,
  normalizeRiskRanking,
} from '../services/riskAssessmentService';
import {
  setAssessmentDetails,
} from '../store/slices/riskAssessmentSlice';

interface UseAssessmentDetailsReturn {
  isLoading: boolean;
  error: Error | null;
  refetchDetails: (silent?: boolean) => Promise<void>;
}

export const useAssessmentDetails = (
  assessmentId: string | undefined
): UseAssessmentDetailsReturn => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Local loading and error states (not in Redux)
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Ref to prevent duplicate fetches
  const hasFetchedRef = useRef(false);
  const currentAssessmentIdRef = useRef<string | undefined>(undefined);

  /**
   * Fetch assessment details from API and update Redux
   */
  const fetchAssessmentDetails = useCallback(async (id: string, silent = false) => {
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);

    try {
      // console.log('🔄 Fetching assessment details for:', id);
      const detailsResponse = await getAssessmentDetails(id);
      const details = detailsResponse.data;

      // Transform to frontend format and dispatch to Redux
      dispatch(setAssessmentDetails({
        assessmentId: details.assessmentId || id,
        quantitativeAssessment: details.quantitativeAssessment || '',
        overallRiskRanking: normalizeRiskRanking(details.overallRiskRanking),
        overallRiskDescription: details.overallRiskDescription || '',
        incentives: details.incentives || '',
        opportunities: details.opportunities || '',
        rationalizations: details.rationalizations || '',
        fraudRiskRanking: details.fraudRiskRanking ? normalizeRiskRanking(details.fraudRiskRanking) : null,
        fraudRiskDescription: details.fraudRiskDescription || '',
      }));

      // console.log('✅ Assessment details loaded');
      return details;
    } catch (err: any) {
      console.error('❌ Failed to fetch assessment details:', err);
      const errorMessage = err?.message || 'Failed to load assessment details';
      setError(new Error(errorMessage));
      if (!silent) {
        message.error(errorMessage);
      }
      throw err;
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [dispatch]);

  /**
   * Refetch assessment details
   * @param silent - If true, don't show loading states or error messages
   */
  const refetchDetails = useCallback(async (silent = false) => {
    if (!assessmentId) {
      console.warn('⚠️ Cannot refetch details: assessmentId is undefined');
      return;
    }
    await fetchAssessmentDetails(assessmentId, silent);
  }, [assessmentId, fetchAssessmentDetails]);

  /**
   * Fetch data when assessmentId changes
   */
  useEffect(() => {
    // Reset state when assessmentId becomes undefined
    if (!assessmentId) {
      dispatch(setAssessmentDetails(null));
      hasFetchedRef.current = false;
      currentAssessmentIdRef.current = undefined;
      setError(null);
      return;
    }

    // Check if we need to fetch (new assessmentId or first time)
    const isNewAssessment = currentAssessmentIdRef.current !== assessmentId;
    
    if (isNewAssessment) {
      // console.log(`🆕 Assessment ID changed: ${currentAssessmentIdRef.current} → ${assessmentId}`);
      hasFetchedRef.current = false;
      currentAssessmentIdRef.current = assessmentId;
    }

    // Skip if already fetched for this assessment
    if (hasFetchedRef.current) {
      // console.log('⏭️ Skipping duplicate fetch for:', assessmentId);
      return;
    }

    // Mark as fetched before fetching to prevent duplicates
    hasFetchedRef.current = true;

    // Fetch assessment details
    fetchAssessmentDetails(assessmentId);
  }, [assessmentId, dispatch, fetchAssessmentDetails]);

  return {
    isLoading,
    error,
    refetchDetails,
  };
};

